import React, { useState } from 'react';
import './AssetDetailModal.css';

interface DepositForm {
  bankName: string;
  amount: string;
  interestRate: number;
  maturityDay: number;
  withholdingTax: number;
  startDate: string;
  currency: string;
}

interface BankDepositProps {
  onDepositAdded?: (deposit: any) => void;
  usdRate?: string;
}

const BankDeposit: React.FC<BankDepositProps> = ({ onDepositAdded, usdRate }) => {
  const [localUsdRate, setLocalUsdRate] = useState(usdRate || '');
  React.useEffect(() => {
    setLocalUsdRate(usdRate || '');
  }, [usdRate]);
  const [form, setForm] = useState<DepositForm>({
    bankName: '',
    amount: '',
    interestRate: 0,
    maturityDay: 0,
    withholdingTax: 0,
    startDate: '',
    currency: 'TRY',
  });
  const [isOpen, setIsOpen] = useState(true);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      // Nokta veya virgül ondalık için izin ver, baştaki sıfırları kaldır
      let sanitized = value.replace(',', '.').replace(/[^0-9.]/g, '');
      // Birden fazla nokta olmasın
      const parts = sanitized.split('.');
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
      }
      // Başındaki sıfırları kaldır (ondalık hariç)
      sanitized = sanitized.replace(/^0+(\d)/, '$1');
      setForm((prev) => ({ ...prev, amount: sanitized }));
      return;
    }
    setForm((prev) => {
      if (["interestRate", "maturityDay", "withholdingTax"].includes(name)) {
        return { ...prev, [name]: value === '' ? 0 : Number(value) };
      }
      return { ...prev, [name]: value };
    });
  };


  // Portföye ekle
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[BankDeposit] Form state:', form);
    // Her ihtimale karşı migration/fix
    let amount = Number(form.amount);
    let buyPrice = amount;
    let currentPrice = amount;
    let quantity = 1;
    // Eğer yanlışlıkla miktar quantity'ye yazılmışsa düzelt
    if (amount === 1 && Number(form.amount) > 1) {
      buyPrice = Number(form.amount);
      currentPrice = Number(form.amount);
      quantity = 1;
    }
    const newDeposit = {
      id: Date.now().toString(),
      type: 'deposit',
      symbol: form.bankName,
      name: form.bankName,
      quantity,
      buyPrice,
      currentPrice,
      totalValue: buyPrice,
      profit: 0,
      profitPercent: 0,
      buyDate: form.startDate,
      usdRate: 1,
      profitUSD: 0,
      profitPercentUSD: 0,
      interestRate: Number(form.interestRate),
      maturityDay: Number(form.maturityDay),
      withholdingTax: Number(form.withholdingTax),
      currency: form.currency,
    };
    console.log('[BankDeposit] Portföye eklenecek yeni mevduat:', newDeposit);
    if (onDepositAdded) {
      onDepositAdded(newDeposit);
    } else {
      // fallback: localStorage
      const portfolio = JSON.parse(localStorage.getItem('portfolio') || '[]');
      localStorage.setItem('portfolio', JSON.stringify([...portfolio, newDeposit]));
    }
    alert('Banka mevduatı kaydedildi!');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  // Faiz hesaplama
  const { amount, interestRate, maturityDay, withholdingTax } = form;
  const amountNum = Number(amount) || 0;
  const grossInterest = amountNum * (interestRate / 100) * (maturityDay / 365);
  const netInterest = grossInterest * (1 - (withholdingTax / 100));
  const totalAtMaturity = amountNum + netInterest;

  return (
    <div className="modal-overlay" style={{zIndex: 1000}}>
      <div className="modal-content asset-detail-modal" style={{maxWidth: 420, padding: 32, borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h2 style={{margin: 0}}>Banka Mevduatı Ekle</h2>
          <button onClick={() => setIsOpen(false)} style={{background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888'}}>×</button>
        </div>
        <div style={{marginBottom: 8, background: '#f6f8fa', borderRadius: 8, padding: 10, fontSize: 15, color: '#444', fontWeight: 500}}>
          Güncel Dolar Kuru: <span style={{color: '#06b6d4', fontWeight: 700}}>{localUsdRate || '-'}</span>
        </div>
        <form onSubmit={handleSubmit} className="deposit-form" style={{display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16}}>
          <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
            <label style={{fontWeight: 700, minWidth: 110}}>Döviz Cinsi</label>
            <select name="currency" value={form.currency} onChange={handleChange} required style={{flex: 1, padding: 8, borderRadius: 8, border: '1px solid #ddd'}}>
              <option value="TRY">Türk Lirası (₺)</option>
              <option value="USD">Amerikan Doları ($)</option>
              <option value="EUR">Euro (€)</option>
              <option value="GBP">Sterlin (£)</option>
              <option value="CHF">İsviçre Frangı (₣)</option>
              <option value="JPY">Japon Yeni (¥)</option>
              <option value="AUD">Avustralya Doları (A$)</option>
              <option value="CAD">Kanada Doları (C$)</option>
              <option value="RUB">Rus Rublesi (₽)</option>
              <option value="SAR">Suudi Riyali (﷼)</option>
              <option value="CNY">Çin Yuanı (¥)</option>
              <option value="other">Diğer</option>
            </select>
          </div>
          <label style={{fontWeight: 500}}>Banka Adı
            <input type="text" name="bankName" value={form.bankName} onChange={handleChange} required style={{width: '100%', padding: 8, borderRadius: 8, border: '1px solid #ddd', marginTop: 4}} />
          </label>
          <label style={{fontWeight: 500}}>
            Tutar ({form.currency === 'TRY' ? '₺' : form.currency === 'USD' ? '$' : form.currency === 'EUR' ? '€' : form.currency === 'GBP' ? '£' : form.currency === 'CHF' ? '₣' : form.currency === 'JPY' ? '¥' : form.currency === 'AUD' ? 'A$' : form.currency === 'CAD' ? 'C$' : form.currency === 'RUB' ? '₽' : form.currency === 'SAR' ? '﷼' : form.currency === 'CNY' ? '¥' : ''})
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              required
              style={{width: '100%', padding: 8, borderRadius: 8, border: '1px solid #ddd', marginTop: 4}}
              placeholder={`Örn: 10000.50 veya 10000,50 ${form.currency !== 'TRY' ? form.currency : ''}`}
            />
          </label>
          <label style={{fontWeight: 500}}>Yıllık Faiz Oranı (%)
            <input type="number" name="interestRate" value={form.interestRate} onChange={handleChange} required style={{width: '100%', padding: 8, borderRadius: 8, border: '1px solid #ddd', marginTop: 4}} />
          </label>
          <label style={{fontWeight: 500}}>Vade Günü
            <input type="number" name="maturityDay" value={form.maturityDay} onChange={handleChange} required style={{width: '100%', padding: 8, borderRadius: 8, border: '1px solid #ddd', marginTop: 4}} />
          </label>
          <label style={{fontWeight: 500}}>Stopaj (%)
            <input type="number" name="withholdingTax" value={form.withholdingTax} onChange={handleChange} required style={{width: '100%', padding: 8, borderRadius: 8, border: '1px solid #ddd', marginTop: 4}} />
          </label>
          <label style={{fontWeight: 500}}>Başlangıç Tarihi
            <input type="date" name="startDate" value={form.startDate} onChange={handleChange} required style={{width: '100%', padding: 8, borderRadius: 8, border: '1px solid #ddd', marginTop: 4}} />
          </label>


          {/* Faiz Sonucu */}
          <div style={{background: '#f6f8fa', borderRadius: 10, padding: 16, marginTop: 8, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'}}>
            <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>Mevduat Sonu Tutarı</div>
            <div style={{fontSize: 22, fontWeight: 700, color: '#06b6d4'}}>₺{totalAtMaturity.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
            <div style={{fontSize: 14, color: '#888'}}>Net faiz kazancı: ₺{netInterest.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
          </div>

          {/* Dolar alsaydık ne olurdu? */}
          <div style={{background: '#e0f2fe', borderRadius: 10, padding: 16, marginTop: 12, textAlign: 'center', boxShadow: '0 1px 4px rgba(6,182,212,0.08)'}}>
            <div style={{fontWeight: 600, fontSize: 16, marginBottom: 4}}>Dolar Alsaydınız Ne Olurdu?</div>
            {(() => {
              const amountNum = Number(form.amount) || 0;
              const usdRateNum = Number(localUsdRate.replace(',', '.')) || 0;
              if (!amountNum || !usdRateNum) return <div style={{color: '#888'}}>Tutar ve güncel dolar kuru girilmelidir.</div>;
              const dollarAmount = amountNum / usdRateNum;
              const tlIfUsd = dollarAmount * usdRateNum; // Vade sonunda kur sabit kabul
              return <>
                <div style={{fontSize: 15, color: '#444', marginBottom: 4}}>
                  {amountNum.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})} TL ile bugünkü kurdan <b>{dollarAmount.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})} $</b> alınabilirdi.
                </div>
                <div style={{fontSize: 15, color: '#444', marginBottom: 4}}>
                  Vade sonunda dolarınızın TL karşılığı (kur değişmezse): <b>₺{tlIfUsd.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</b>
                </div>
                <div style={{fontSize: 13, color: '#888'}}>Not: Hesaplama sabit kur varsayımıyla yapılır.</div>
              </>;
            })()}
          </div>

          <button type="submit" style={{marginTop: 12, padding: '10px 0', borderRadius: 8, background: 'linear-gradient(90deg,#3b82f6,#06b6d4)', color: '#fff', fontWeight: 600, fontSize: 16, border: 'none', cursor: 'pointer', boxShadow: '0 2px 8px rgba(59,130,246,0.08)'}}>Kaydet</button>
        </form>
      </div>
    </div>
  );
};

export default BankDeposit;
